import { AddressComponentConfig, DEFAULT_ADDRESS_CONFIG } from '@/map/domain/entities/AddressComponentConfig';
import { ComponentType, IGoogleMapsService } from '@/map/domain/services/IGoogleMapsService';

import { PlaceDetails } from '@/map/domain/entities/map.types';

export class GoogleMapsService implements IGoogleMapsService {
  async handleGoogleMapsUrl(
    googleMapsCoords: { lat: number; lng: number; placeName?: string },
    map: google.maps.Map | null,
    setMarkerPosition: (position: google.maps.LatLngLiteral) => void,
    updateFormValues: (details: PlaceDetails) => Promise<void>
  ): Promise<void> {
    const geocoder = new google.maps.Geocoder();
    try {
      const bounds = new google.maps.LatLngBounds(
        { lat: googleMapsCoords.lat - 0.01, lng: googleMapsCoords.lng - 0.01 },
        { lat: googleMapsCoords.lat + 0.01, lng: googleMapsCoords.lng + 0.01 }
      );

      const result = await geocoder.geocode({
        address: googleMapsCoords.placeName,
        bounds: bounds
      });

      if (result.results[0]) {
        const location = result.results[0].geometry.location;
        const newPosition = {
          lat: location.lat(),
          lng: location.lng()
        };

        if (map) {
          map.panTo(newPosition);
          map.setZoom(15);
        }
        setMarkerPosition(newPosition);

        const addressComponents = result.results[0].address_components || [];
        const details = this.extractAddressComponents(addressComponents);

        const newDetails = {
          ...details,
          latitude: Number(newPosition.lat.toFixed(6)),
          longitude: Number(newPosition.lng.toFixed(6))
        };

        await updateFormValues(newDetails);
      }
    } catch {
      // Handle error silently
    }
  }

  async handleDirectCoordinates(
    coordinates: { lat: number; lng: number },
    map: google.maps.Map | null,
    setMarkerPosition: (position: google.maps.LatLngLiteral) => void,
    updateFormValues: (details: PlaceDetails) => Promise<void>
  ): Promise<void> {
    if (map) {
      map.panTo(coordinates);
      map.setZoom(15);
    }
    setMarkerPosition(coordinates);

    const newLatitude = Number(coordinates.lat.toFixed(6));
    const newLongitude = Number(coordinates.lng.toFixed(6));

    const geocoder = new google.maps.Geocoder();
    try {
      const result = await geocoder.geocode({ location: coordinates });
      if (result.results[0]) {
        const addressComponents = result.results[0].address_components || [];
        const details = this.extractAddressComponents(addressComponents);

        const newDetails = {
          ...details,
          latitude: newLatitude,
          longitude: newLongitude
        };

        await updateFormValues(newDetails);
      }
    } catch {
      // Handle error silently
    }
  }

  async handlePlacesApiSearch(
    value: string,
    map: google.maps.Map | null,
    sessionToken: google.maps.places.AutocompleteSessionToken | null,
    setIsLoading: (loading: boolean) => void,
    setSuggestions: (suggestions: google.maps.places.AutocompleteSuggestion[]) => void
  ): Promise<void> {
    if (!sessionToken) return;

    try {
      setIsLoading(true);
      const bounds = map?.getBounds();
      const request = {
        input: value,
        sessionToken,
        locationBias: bounds
          ? {
              west: bounds.getSouthWest().lng(),
              north: bounds.getNorthEast().lat(),
              east: bounds.getNorthEast().lng(),
              south: bounds.getSouthWest().lat()
            }
          : undefined
      };

      const { suggestions: newSuggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      setSuggestions(newSuggestions);
    } catch {
      // Handle error silently
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }

  parseCoordinates(input: string): { lat: number; lng: number } | null {
    const coordRegex = /(-?\d+[.,]\d+)\s*,\s*(-?\d+[.,]\d+)/;
    const coordMatch = coordRegex.exec(input);
    if (coordMatch?.[1] && coordMatch[2]) {
      const lat = parseFloat(coordMatch[1].replace(',', '.'));
      const lng = parseFloat(coordMatch[2].replace(',', '.'));
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return null;
  }

  parseGoogleMapsUrl(input: string): { lat: number; lng: number; placeName?: string } | null {
    const placeRegex = /\/place\/([^/@]+)\/@(-?\d+[.,]\d+),(-?\d+[.,]\d+)/;
    const placeMatch = placeRegex.exec(input);
    if (placeMatch?.[2] && placeMatch[3]) {
      const lat = parseFloat(placeMatch[2].replace(',', '.'));
      const lng = parseFloat(placeMatch[3].replace(',', '.'));
      if (!isNaN(lat) && !isNaN(lng)) {
        const placeName = placeMatch[1] ? decodeURIComponent(placeMatch[1].replace(/\+/g, ' ')) : undefined;
        return { lat, lng, placeName };
      }
    }
    return null;
  }

  // --- Address Extraction Methods ---
  private getAddressComponentValue(component: ComponentType): string | undefined {
    return (component as unknown as { long_name?: string }).long_name ?? undefined;
  }

  private getAddressComponentShortValue(component: ComponentType): string | undefined {
    return (component as unknown as { short_name?: string }).short_name ?? undefined;
  }

  private getComponentValue(component: ComponentType, fieldConfig: AddressComponentConfig[keyof AddressComponentConfig]): string {
    const value = fieldConfig.useShortName ? this.getAddressComponentShortValue(component) : this.getAddressComponentValue(component);
    return value || fieldConfig.fallback || '';
  }

  private findComponentByTypes(addressComponents: ComponentType[], types: string[]): ComponentType | undefined {
    return addressComponents.find((component) => component.types.some((type) => types.includes(type)));
  }

  public extractAddressComponents(
    addressComponents: ComponentType[],
    config: AddressComponentConfig = DEFAULT_ADDRESS_CONFIG
  ): PlaceDetails {
    const details: PlaceDetails = {};

    // Helper function to extract field with fallbacks
    const extractFieldWithFallbacks = (fieldName: keyof AddressComponentConfig, detailsKey: keyof PlaceDetails) => {
      let component = this.findComponentByTypes(addressComponents, config[fieldName].types);
      if (!component && config[fieldName].alternativeTypes) {
        component = this.findComponentByTypes(addressComponents, config[fieldName].alternativeTypes!);
      }
      if (component) {
        (details as Record<string, string>)[detailsKey as string] = this.getComponentValue(component, config[fieldName]);
      }
    };

    // Special handling for address field - combine street number and route
    const streetNumberComponent = this.findComponentByTypes(addressComponents, ['street_number']);
    const routeComponent = this.findComponentByTypes(addressComponents, ['route']);

    if (streetNumberComponent || routeComponent) {
      const streetNumber = streetNumberComponent ? this.getAddressComponentValue(streetNumberComponent) : '';
      const route = routeComponent ? this.getAddressComponentValue(routeComponent) : '';

      // Combine street number and route into a single address
      const address = [streetNumber, route].filter(Boolean).join(' ');
      if (address) {
        details.address = address;
      }
    } else {
      // Fallback: try to extract address using alternative types
      extractFieldWithFallbacks('address', 'address');
    }

    // Extract other fields with fallbacks
    extractFieldWithFallbacks('city', 'city');
    extractFieldWithFallbacks('state', 'state');
    extractFieldWithFallbacks('zipCode', 'zipCode');
    extractFieldWithFallbacks('country', 'country');

    return details;
  }

  /**
   * Pan map to position with offset for small screens to account for overlay forms
   * Adjusts the map center so that the marker appears in the visible area when a form overlay is present
   * @param map Google Maps instance
   * @param position Target position to pan to
   * @param isSmallScreen Whether the screen is small (typically < 950px)
   * @param mapWidth Width of the map container in pixels
   * @param formOffset Offset of the form overlay from the left edge in pixels (default: 410)
   */
  panToWithOffset(
    map: google.maps.Map | null,
    position: google.maps.LatLngLiteral,
    isSmallScreen: boolean,
    mapWidth: number,
    formOffset: number = 410
  ): void {
    if (!map) return;

    if (isSmallScreen) {
      // Use a simpler approach: calculate offset based on viewport bounds
      const bounds = map.getBounds();
      if (!bounds) {
        map.panTo(position);
        return;
      }

      // Get bounds to calculate the visible longitude range
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const lngRange = ne.lng() - sw.lng();

      // Calculate what percentage of the map width the form occupies
      const formPercentage = formOffset / mapWidth;

      // Calculate longitude offset (shift center left so pin appears right)
      const lngOffset = lngRange * formPercentage * 0.5; // 0.5 to account for center positioning

      // Adjust center to shift pin to the right
      const adjustedCenter = {
        lat: position.lat,
        lng: position.lng - lngOffset
      };

      map.panTo(adjustedCenter);
    } else {
      map.panTo(position);
    }
  }
}
